import { EventType, type AuthenticationResult } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeConfig, ThemeProvider } from 'flowbite-react';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { App } from './App.js';
import './index.css';
import { createMsalInstance } from './lib/msal-config.js';
import { customTheme } from './theme.js';

// `VITE_E2E_BYPASS` is replaced at build time. When unset (production
// + every CI build that doesn't opt in), the dynamic import inside
// `bootstrap()` is unreachable and Vite tree-shakes the stub out — the
// e2e stub never ships to operators. Only the dedicated playwright
// build (`pnpm test:e2e`) sets the flag to '1'.
const E2E_BYPASS = import.meta.env.VITE_E2E_BYPASS === '1';

const msalInstance = createMsalInstance();
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Tokens auto-refresh via MSAL; keep query staleness short enough that
      // role/permission changes show up quickly but don't thrash the API.
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// Keep the active account in sync with the latest sign-in. Without this
// MSAL won't know which account to use for silent token acquisition, and
// useIsAuthenticated() can stay false even after a successful redirect.
msalInstance.addEventCallback((event) => {
  if (
    (event.eventType === EventType.LOGIN_SUCCESS ||
      event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS) &&
    event.payload &&
    'account' in event.payload &&
    event.payload.account
  ) {
    msalInstance.setActiveAccount((event.payload as AuthenticationResult).account);
  }
});

async function bootstrap(): Promise<void> {
  const rootEl = document.getElementById('root');
  if (!rootEl) {
    throw new Error('Root element #root missing from index.html');
  }

  // E2E builds swap the real MSAL instance for a stub that returns a
  // canned account + fake bearer token; see e2e-msal-stub.ts. Dynamic
  // import behind a literal-false guard in production builds so the
  // stub doesn't ship.
  const instance = E2E_BYPASS
    ? (await import('./lib/e2e-msal-stub.js')).createE2EMsalStub()
    : msalInstance;

  // Initialize first — required by msal-browser v3+ before any other call.
  await instance.initialize();

  // Then process any pending redirect response (the auth code MSAL stashed
  // in window.location.hash). This must complete BEFORE we mount the React
  // tree, otherwise the StrictMode double-render races with the internal
  // handleRedirectPromise inside MsalProvider and the hash can get blown
  // away before MSAL reads it.
  const response = await instance.handleRedirectPromise();
  if (response?.account) {
    instance.setActiveAccount(response.account);
  } else {
    const cached = instance.getAllAccounts()[0];
    if (cached) instance.setActiveAccount(cached);
  }

  createRoot(rootEl).render(
    <StrictMode>
      <ThemeConfig mode="dark" />
      <ThemeProvider theme={customTheme}>
        <MsalProvider instance={instance}>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </QueryClientProvider>
        </MsalProvider>
      </ThemeProvider>
    </StrictMode>,
  );
}

void bootstrap();
